// --------------
// IDENTIFIKATIONSDATEN

import { IdentifikationsnachweisAusweisart } from '@mng/domain';
import {
  createValidationResult,
  DateField,
  DependencyFunction,
  IModelContext,
  ISimpleValidationResult,
  IValidationResult,
  ModelDependency,
  ModelField,
  ModelGroup,
  ModelItem,
  ModelItemOptions,
  StringField,
  ValidationFunction,
  ValueOptions
} from '@mng/model';
import { dateToIsoDateString, getAlterForGeburtstag, getYearFromDate, isValidISODate } from '@mng/mng-ts-helpers';
import { Antragdata, Identifikationsdaten, Person } from '../../antrag.model.types';
import {
  findAntragdataInContext,
  findPersonInContext,
  hatPersonLvAngebotsBeziehung,
  isForNatuerlichePersonRequired,
  isPersonJuristisch
} from '../logic/person-model.logic';
import { antragContext } from '../antrag-context';
import { IAntragFragen } from '../logic/antrag-fragen';

export const ausweisartValueOptions: ValueOptions = new ValueOptions([
  { name: 'Führerschein', value: IdentifikationsnachweisAusweisart.FUEHRERSCHEIN },
  { name: 'Reisepass', value: IdentifikationsnachweisAusweisart.REISEPASS },
  { name: 'Personalausweis', value: IdentifikationsnachweisAusweisart.PERSONALAUSWEIS },
  { name: 'Öffentlicher Dienstausweis', value: IdentifikationsnachweisAusweisart.OEFFENTLICHER_DIENSTAUSWEIS },
  { name: 'E-Card', value: IdentifikationsnachweisAusweisart.ECARD },
  { name: 'Schülerausweis', value: IdentifikationsnachweisAusweisart.SCHUELERAUSWEIS },
  { name: 'Geburtsurkunde', value: IdentifikationsnachweisAusweisart.GEBURTSURKUNDE }
]);

export class IdentifikationsnachweisModelDef {
  static createIdentifikationsEditMetaDatenModel(): ModelGroup {
    const modelGroup: ModelGroup = this.createIdentifikationsMetaDatenModel(false);
    const ausweisartField = <StringField>modelGroup.getItemById('ausweisart');
    ausweisartField.options.withValueOptions(ausweisartValueOptions);

    modelGroup.fields.forEach((field: ModelField) => field.withReadOnly(false));
    return modelGroup;
  }

  static createIdentifikationsMetaDatenModel(readOnly = true): ModelGroup {
    const ausweisartField: StringField = new StringField('ausweisart', 'Ausweisart')
      .withFunctionDependency(isForNatuerlichePersonRequired)
      .withValidation(isAusweisartRequired)
      .withReadOnly(readOnly);

    const ausweisNrField: StringField = new StringField('ausweisNr', 'Ausweisnummer')
      .withFunctionDependency(isAusweisnummerRequired)
      .withReadOnly(readOnly);

    const ausstellungsdatumField: DateField = new DateField('ausstellungsdatum', 'Ausstellungsdatum')
      .withFunctionDependency(isForNatuerlichePersonRequired, false)
      .withValidation(ausstellungsDatumValid)
      .withReadOnly(readOnly);

    const ausstellendeBehoerdeField: StringField = new StringField('ausstellendeBehoerde', 'Austellende Behörde')
      .withFunctionDependency(isAusstellendeBehoerdeRequired)
      .withReadOnly(readOnly);

    const firmenbuchNrField: StringField = new StringField('firmenbuchNr', 'Firmenbuchnummer')
      .withFunctionDependency(isForJuristischePersonRequired, false)
      .withValidation(firmenbuchNrValid)
      .withReadOnly(readOnly);

    const identifikationsnachweisGroup: ModelGroup = new ModelGroup(
      'identifikationsnachweis',
      'Identifikationsnachweis'
    ).addItems(ausweisartField, ausweisNrField, ausstellungsdatumField, ausstellendeBehoerdeField, firmenbuchNrField);
    identifikationsnachweisGroup.options = new ModelItemOptions([identifikationsDatenValidation]);
    return identifikationsnachweisGroup;
  }
}

export function isAusstellungsdatumValid(ausstellungsdatum: string): ISimpleValidationResult {
  const result: ISimpleValidationResult = { valid: true };

  if (ausstellungsdatum && isValidISODate(ausstellungsdatum)) {
    // Prüfung of ausstellungsdatum > Tagesdatum
    if (ausstellungsdatum > dateToIsoDateString()) {
      result.valid = false;
      result.message = 'Austellungsdatum darf nicht in der Zukunft liegen';
    }

    if (result.valid) {
      // Prüfung - Jahr darf nicht vor 1900 liegen

      const jahr: string = getYearFromDate(ausstellungsdatum);
      if (jahr && jahr < '1900') {
        result.valid = false;
        result.message = 'Austellungsjahr darf nicht von 1900 liegen';
      }
    }
  }
  return result;
}

export const ausstellungsDatumValid: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext
): IValidationResult => {
  if (!modelContext.value) {
    return null;
  }

  const simpleValidationResult: ISimpleValidationResult = isAusstellungsdatumValid(modelContext.value);

  if (simpleValidationResult.valid) {
    return null;
  } else {
    return { id: 'ausstellungsDatum', message: simpleValidationResult.message };
  }
};

export const identifikationsDatenValidation: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext
): IValidationResult => {
  const data: Identifikationsdaten = modelContext.value;
  const antrag: Antragdata = findAntragdataInContext(modelContext);
  const person: Person = findPersonInContext(modelContext);
  const isLVAntrag: boolean = hatPersonLvAngebotsBeziehung(person);
  const fragen: IAntragFragen = antragContext.getFragen(antrag, person);

  let valid = !!data;
  let message: string = null;

  if (valid) {
    if (person && person.juristischePerson) {
      // JURISTISCHE PERSONEN
      if (isLVAntrag) {
        valid = !!data.firmenbuchNr;
      }
    } else {
      // NATÜRLICHE PERSONEN
      valid = !!data.ausweisart && data.ausweisart !== IdentifikationsnachweisAusweisart.UNBEKANNT;

      if (fragen.identifikationsnachweis !== true) {
        valid = true;
      }

      if (valid && !!data.ausweisart && data.ausweisart !== IdentifikationsnachweisAusweisart.UNBEKANNT) {
        const simpleValidationResult: ISimpleValidationResult = isAusweisartForAlterOk(person, data);
        valid = simpleValidationResult.valid;
        message = simpleValidationResult.message;
      }
    }
  }

  const result: IValidationResult = valid ? null : createValidationResult('identifikationsnachweis', null, message);

  // console.log('identifikationsDatenValidation', result);

  return result;
};

const isForJuristischePersonRequired: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const person: Person = findPersonInContext(modelContext);
  const isJuristischePerson = isPersonJuristisch(person);
  return isJuristischePerson;
};

export const BEHOERDEN_AUSWEISARTEN = [
  IdentifikationsnachweisAusweisart.REISEPASS,
  IdentifikationsnachweisAusweisart.PERSONALAUSWEIS,
  IdentifikationsnachweisAusweisart.FUEHRERSCHEIN,
  IdentifikationsnachweisAusweisart.GEBURTSURKUNDE,
  IdentifikationsnachweisAusweisart.OEFFENTLICHER_DIENSTAUSWEIS
];

function isBehoerdenAusweisart(identifikationsdaten: Identifikationsdaten): boolean {
  return isAusweisArtIn(identifikationsdaten, BEHOERDEN_AUSWEISARTEN);
}

function isAusweisArtIn(identifikationsdaten: Identifikationsdaten, ausweistArten: string[]): boolean {
  return identifikationsdaten && !!ausweistArten.find((art: string) => art === identifikationsdaten.ausweisart);
}

function isAusweisartForAlterOk(person: Person, identifikationsdaten: Identifikationsdaten): ISimpleValidationResult {
  let valid = true;
  let message: string = null;
  const alter: number = getAlterForGeburtstag(person.stammdaten.gebdatum);

  if (alter === null) {
    valid = false;
  }

  if (valid && identifikationsdaten.ausweisart === IdentifikationsnachweisAusweisart.FUEHRERSCHEIN) {
    if (alter <= 13) {
      valid = false;
      message = 'Führerschein darf erst für Personen ab 14 Jahren verwendet werden';
    }
  }

  if (valid) {
    if (
      !isAusweisArtIn(identifikationsdaten, [
        IdentifikationsnachweisAusweisart.REISEPASS,
        IdentifikationsnachweisAusweisart.PERSONALAUSWEIS,
        IdentifikationsnachweisAusweisart.FUEHRERSCHEIN,
        IdentifikationsnachweisAusweisart.OEFFENTLICHER_DIENSTAUSWEIS
      ]) &&
      alter >= 18
    ) {
      valid = false;
      message = 'Person muss jünger als 18 Jahre sein';
    }
  }

  return { valid, message };
}

function isEcard(identifikationsdaten: Identifikationsdaten): boolean {
  return identifikationsdaten && identifikationsdaten.ausweisart === IdentifikationsnachweisAusweisart.ECARD;
}

function isGeburtsurkunde(identifikationsdaten: Identifikationsdaten): boolean {
  return identifikationsdaten && identifikationsdaten.ausweisart === IdentifikationsnachweisAusweisart.GEBURTSURKUNDE;
}

function isSchuelerAusweis(identifikationsdaten: Identifikationsdaten): boolean {
  return identifikationsdaten && identifikationsdaten.ausweisart === IdentifikationsnachweisAusweisart.SCHUELERAUSWEIS;
}

const isAusweisnummerRequired: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const person: Person = findPersonInContext(modelContext);

  if (isPersonJuristisch(person)) {
    return false;
  }

  const identifikationsdaten: Identifikationsdaten = modelContext.dataStack[modelContext.dataStack.length - 1];
  return (
    (isBehoerdenAusweisart(identifikationsdaten) && !isGeburtsurkunde(identifikationsdaten)) ||
    isEcard(identifikationsdaten)
  );
};

const isAusstellendeBehoerdeRequired: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const person: Person = findPersonInContext(modelContext);

  if (isPersonJuristisch(person)) {
    return false;
  }

  const identifikationsdaten: Identifikationsdaten = modelContext.dataStack[modelContext.dataStack.length - 1];
  return isBehoerdenAusweisart(identifikationsdaten) || isSchuelerAusweis(identifikationsdaten);
};

export const firmenbuchNrValid: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext
): IValidationResult => {
  const person: Person = findPersonInContext(modelContext);
  const simpleValidationResult: ISimpleValidationResult = isFirmenbuchNrValid(modelContext.value, person);

  if (simpleValidationResult.valid) {
    return null;
  } else {
    return {
      id: 'firmenbuchNr',
      message: simpleValidationResult.message
    };
  }
};

export function isFirmenbuchNrValid(firmenbuchNr: string, person: Person): ISimpleValidationResult {
  const result: ISimpleValidationResult = { valid: true };
  const isLVAntrag: boolean = hatPersonLvAngebotsBeziehung(person);
  if (isLVAntrag && !firmenbuchNr) {
    result.valid = false;
    result.message = 'Firmenbuchnummer muss bekanntgegeben werden';
  }
  return result;
}

export const isAusweisartRequired: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext = {}
): IValidationResult => {
  const antrag: Antragdata = findAntragdataInContext(modelContext);
  const person: Person = findPersonInContext(modelContext);
  const simpleValidationResult: ISimpleValidationResult = isAusweisartValid(modelContext.value, antrag, person);

  if (simpleValidationResult.valid) {
    return null;
  } else {
    return {
      id: 'ausweisart',
      message: simpleValidationResult.message
    };
  }
};

export function isAusweisartValid(ausweisart: string, antrag: Antragdata, person: Person): ISimpleValidationResult {
  const result: ISimpleValidationResult = { valid: true };
  const fragen: IAntragFragen = antragContext.getFragen(antrag, person);
  if (fragen.identifikationsnachweis && !ausweisart) {
    result.valid = false;
    result.message = 'Ausweisart muss eingegeben werden';
  }
  return result;
}
